/**
 * Builder appearance runtime, ported from the Pulse demo
 * (blazorly-ux-design/src/lib/appearance.ts).
 *
 * Themes are HSL channel strings written onto <html> at runtime.
 * Tailwind v4 already maps those variables in index.css, so this file
 * does not ship a Tailwind config.
 */

export type TextSize = 'sm' | 'md' | 'lg' | 'xl';
export type RadiusMode = 'sharp' | 'soft' | 'round' | 'pill';

export interface Appearance {
	theme: string;
	accent: string;
	customAccent: string;
	font: string;
	textSize: TextSize;
	radius: RadiusMode;
	reduceMotion: boolean;
}

export const APPEARANCE_STORAGE_KEY = 'builder.appearance.v1';
export const LEGACY_THEME_KEY = 'theme';

export const DEFAULT_APPEARANCE: Appearance = {
	theme: 'system',
	accent: 'iris',
	customAccent: '#6d4aff',
	font: 'playful',
	textSize: 'md',
	radius: 'round',
	reduceMotion: false,
};

export interface ThemeDef {
	id: string;
	label: string;
	blurb: string;
	dark: boolean;
	accent: string;
	v: {
		bg: string;
		fg: string;
		card: string;
		secondary: string;
		muted: string;
		mutedFg: string;
		border: string;
		input: string;
	};
}

export const THEMES: ThemeDef[] = [
	{ id: 'cream', label: 'Cream', blurb: 'Warm & cozy', dark: false, accent: 'iris', v: { bg: '38 40% 97%', fg: '250 28% 12%', card: '0 0% 100%', secondary: '38 30% 93%', muted: '38 25% 92%', mutedFg: '250 10% 42%', border: '36 20% 87%', input: '36 20% 85%' } },
	{ id: 'paper', label: 'Paper', blurb: 'Clean neutral', dark: false, accent: 'mono', v: { bg: '0 0% 98%', fg: '0 0% 9%', card: '0 0% 100%', secondary: '0 0% 94%', muted: '0 0% 94%', mutedFg: '0 0% 42%', border: '0 0% 88%', input: '0 0% 86%' } },
	{ id: 'mist', label: 'Mist', blurb: 'Cool & airy', dark: false, accent: 'sky', v: { bg: '215 33% 97%', fg: '222 40% 12%', card: '0 0% 100%', secondary: '214 30% 93%', muted: '214 28% 92%', mutedFg: '218 12% 42%', border: '214 22% 87%', input: '214 22% 84%' } },
	{ id: 'blossom', label: 'Blossom', blurb: 'Soft pink', dark: false, accent: 'rose', v: { bg: '340 60% 97%', fg: '335 30% 14%', card: '0 0% 100%', secondary: '340 40% 94%', muted: '340 35% 93%', mutedFg: '335 10% 42%', border: '340 30% 89%', input: '340 30% 86%' } },
	{ id: 'sage', label: 'Sage', blurb: 'Calm green', dark: false, accent: 'mint', v: { bg: '140 25% 96%', fg: '150 25% 10%', card: '0 0% 100%', secondary: '140 20% 92%', muted: '140 18% 91%', mutedFg: '150 8% 40%', border: '140 15% 86%', input: '140 15% 83%' } },
	{ id: 'midnight', label: 'Midnight', blurb: 'Deep violet', dark: true, accent: 'iris', v: { bg: '250 26% 7%', fg: '40 30% 94%', card: '250 22% 11%', secondary: '250 18% 16%', muted: '250 16% 15%', mutedFg: '250 10% 66%', border: '250 14% 19%', input: '250 14% 22%' } },
	{ id: 'graphite', label: 'Graphite', blurb: 'Sleek dark gray', dark: true, accent: 'mono', v: { bg: '0 0% 8%', fg: '0 0% 95%', card: '0 0% 11.5%', secondary: '0 0% 16%', muted: '0 0% 15%', mutedFg: '0 0% 64%', border: '0 0% 19%', input: '0 0% 22%' } },
	{ id: 'obsidian', label: 'Obsidian', blurb: 'True black', dark: true, accent: 'mono', v: { bg: '0 0% 0%', fg: '0 0% 96%', card: '0 0% 6%', secondary: '0 0% 11%', muted: '0 0% 10%', mutedFg: '0 0% 62%', border: '0 0% 15%', input: '0 0% 18%' } },
	{ id: 'forest', label: 'Forest', blurb: 'Mossy dark', dark: true, accent: 'mint', v: { bg: '160 25% 6%', fg: '140 20% 93%', card: '160 22% 9.5%', secondary: '160 18% 14%', muted: '160 16% 13%', mutedFg: '150 10% 62%', border: '160 14% 17%', input: '160 14% 20%' } },
	{ id: 'ocean', label: 'Ocean', blurb: 'Deep blue', dark: true, accent: 'sky', v: { bg: '220 40% 7%', fg: '210 30% 94%', card: '220 35% 10.5%', secondary: '220 28% 15%', muted: '220 26% 14%', mutedFg: '215 14% 64%', border: '220 22% 18%', input: '220 22% 21%' } },
	{ id: 'ember', label: 'Ember', blurb: 'Warm dark', dark: true, accent: 'coral', v: { bg: '15 22% 7%', fg: '30 30% 94%', card: '15 20% 10.5%', secondary: '15 16% 15%', muted: '15 14% 14%', mutedFg: '20 10% 64%', border: '15 12% 18%', input: '15 12% 21%' } },
	{ id: 'terminal', label: 'Terminal', blurb: 'Hacker green', dark: true, accent: 'matrix', v: { bg: '130 30% 4%', fg: '130 90% 78%', card: '130 28% 7%', secondary: '130 22% 12%', muted: '130 20% 11%', mutedFg: '130 25% 52%', border: '130 30% 16%', input: '130 30% 19%' } },
];

function themeById(id: string): ThemeDef {
	const theme = THEMES.find((item) => item.id === id) ?? THEMES[0];
	if (!theme) throw new Error('No appearance themes are configured');
	return theme;
}

export function resolveTheme(appearance: Appearance, prefersDark: boolean): ThemeDef {
	const id = appearance.theme === 'system' ? (prefersDark ? 'midnight' : 'cream') : appearance.theme;
	return themeById(id);
}

export interface AccentDef {
	id: string;
	label: string;
	h: number;
	s: number;
	l: number;
	ld: number;
}

export const ACCENTS: AccentDef[] = [
	{ id: 'iris', label: 'Iris', h: 256, s: 80, l: 60, ld: 70 },
	{ id: 'coral', label: 'Coral', h: 12, s: 90, l: 56, ld: 64 },
	{ id: 'rose', label: 'Rose', h: 335, s: 80, l: 55, ld: 66 },
	{ id: 'sun', label: 'Sunny', h: 40, s: 96, l: 48, ld: 56 },
	{ id: 'lime', label: 'Lime', h: 85, s: 70, l: 38, ld: 52 },
	{ id: 'mint', label: 'Mint', h: 160, s: 75, l: 34, ld: 48 },
	{ id: 'matrix', label: 'Matrix', h: 135, s: 90, l: 36, ld: 50 },
	{ id: 'sky', label: 'Sky', h: 208, s: 92, l: 48, ld: 60 },
	{ id: 'indigo', label: 'Indigo', h: 228, s: 80, l: 55, ld: 68 },
	{ id: 'mono', label: 'Mono', h: 250, s: 8, l: 14, ld: 92 },
];

export function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
	const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
	const hexBody = match?.[1];
	if (!hexBody) return null;
	const value = parseInt(hexBody, 16);
	const r = ((value >> 16) & 255) / 255;
	const g = ((value >> 8) & 255) / 255;
	const b = (value & 255) / 255;
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;
	const d = max - min;
	let h = 0;
	let s = 0;
	if (d) {
		s = d / (1 - Math.abs(2 * l - 1));
		if (max === r) h = ((g - b) / d) % 6;
		else if (max === g) h = (b - r) / d + 2;
		else h = (r - g) / d + 4;
		h = Math.round(h * 60);
		if (h < 0) h += 360;
	}
	return { h, s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function accentCss(appearance: Appearance, theme: ThemeDef): { h: number; s: number; l: number } {
	if (appearance.accent === 'custom') {
		const custom = hexToHsl(appearance.customAccent) ?? { h: 256, s: 80, l: 60 };
		return {
			h: custom.h,
			s: Math.max(custom.s, 20),
			l: theme.dark ? Math.max(custom.l, 58) : Math.min(Math.max(custom.l, 28), 62),
		};
	}
	const id = appearance.accent === 'theme' ? theme.accent : appearance.accent;
	const accent = ACCENTS.find((item) => item.id === id) ?? ACCENTS[0];
	if (!accent) return { h: 256, s: 80, l: theme.dark ? 70 : 60 };
	return { h: accent.h, s: accent.s, l: theme.dark ? accent.ld : accent.l };
}

export function accentPreview(id: string, dark = false) {
	const accent = ACCENTS.find((item) => item.id === id) ?? ACCENTS[0];
	if (!accent) return 'hsl(256 80% 60%)';
	return `hsl(${accent.h} ${accent.s}% ${dark ? accent.ld : accent.l}%)`;
}

export function relativeLuminance(h: number, s: number, l: number) {
	const saturation = s / 100;
	const lightness = l / 100;
	const k = (n: number) => (n + h / 30) % 12;
	const a = saturation * Math.min(lightness, 1 - lightness);
	const f = (n: number) => lightness - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
	const linear = (channel: number) => (channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4));
	return 0.2126 * linear(f(0)) + 0.7152 * linear(f(8)) + 0.0722 * linear(f(4));
}

/**
 * Gradient stops stay in the accent's own hue.
 * A large hue jump painted terra and green onto themes that were neither.
 */
export function brandChannels(h: number, s: number, l: number): { brand2: string; brand3: string } {
	const sat = Math.min(s, 88);
	const shift = s < 20 ? 0 : 12;
	return {
		brand2: `${(h + shift) % 360} ${sat}% ${Math.min(l + 6, 74)}%`,
		brand3: `${(h + shift * 2) % 360} ${sat}% ${Math.max(l - 4, 28)}%`,
	};
}

function toneChannel(h: number, s: number, lightness: number): string {
	return `${h} ${Math.min(s, 72)}% ${lightness}%`;
}

export interface FontPair {
	id: string;
	label: string;
	blurb: string;
	sans: string;
	display: string;
	tracking: string;
}

const SYSTEM_FONT = 'ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif';

export const FONTS: FontPair[] = [
	{ id: 'playful', label: 'Playful', blurb: 'Bricolage + Inter', sans: `Inter, ${SYSTEM_FONT}`, display: `"Bricolage Grotesque", Inter, ${SYSTEM_FONT}`, tracking: '-0.02em' },
	{ id: 'clean', label: 'Clean', blurb: 'Inter', sans: `Inter, ${SYSTEM_FONT}`, display: `Inter, ${SYSTEM_FONT}`, tracking: '-0.015em' },
	{ id: 'friendly', label: 'Friendly', blurb: 'Nunito', sans: `Nunito, ${SYSTEM_FONT}`, display: `Nunito, ${SYSTEM_FONT}`, tracking: '0em' },
	{ id: 'techy', label: 'Techy', blurb: 'Space Grotesk + DM Sans', sans: `"DM Sans", ${SYSTEM_FONT}`, display: `"Space Grotesk", "DM Sans", ${SYSTEM_FONT}`, tracking: '-0.02em' },
	{ id: 'editorial', label: 'Editorial', blurb: 'Fraunces + Lora', sans: 'Lora, Georgia, serif', display: 'Fraunces, Georgia, serif', tracking: '-0.01em' },
	{ id: 'mono', label: 'Mono', blurb: 'JetBrains Mono', sans: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace', display: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace', tracking: '-0.03em' },
	{ id: 'system', label: 'System', blurb: 'Your device font', sans: SYSTEM_FONT, display: SYSTEM_FONT, tracking: '-0.01em' },
];

const FONT_STYLESHEETS: Record<string, string> = {
	playful: 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,600;12..96,700;12..96,800&family=Inter:wght@400;500;600;700&display=swap',
	clean: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
	friendly: 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&display=swap',
	techy: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Space+Grotesk:wght@500;700&display=swap',
	editorial: 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=Lora:wght@400;600&display=swap',
	mono: 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&display=swap',
};

export const TEXT_SIZES: { id: TextSize; label: string; px: number }[] = [
	{ id: 'sm', label: 'Compact', px: 14 },
	{ id: 'md', label: 'Default', px: 16 },
	{ id: 'lg', label: 'Large', px: 17.5 },
	{ id: 'xl', label: 'Extra large', px: 19 },
];

export const RADII: { id: RadiusMode; label: string; radius: string; btn: string }[] = [
	{ id: 'sharp', label: 'Sharp', radius: '0.375rem', btn: '0.375rem' },
	{ id: 'soft', label: 'Soft', radius: '0.75rem', btn: '0.75rem' },
	{ id: 'round', label: 'Round', radius: '1rem', btn: '9999px' },
	{ id: 'pill', label: 'Bubbly', radius: '1.5rem', btn: '9999px' },
];

export const QUICK_LOOKS: { label: string; emoji: string; patch: Partial<Appearance> }[] = [
	{ label: 'Cozy', emoji: '🧸', patch: { theme: 'cream', accent: 'iris', font: 'playful', radius: 'pill', textSize: 'md' } },
	{ label: 'Focus', emoji: '🎯', patch: { theme: 'paper', accent: 'mono', font: 'clean', radius: 'soft', textSize: 'md' } },
	{ label: 'Hacker', emoji: '👾', patch: { theme: 'terminal', accent: 'matrix', font: 'mono', radius: 'sharp', textSize: 'sm' } },
	{ label: 'Sunset', emoji: '🌅', patch: { theme: 'ember', accent: 'coral', font: 'friendly', radius: 'round', textSize: 'md' } },
	{ label: 'Ocean', emoji: '🌊', patch: { theme: 'ocean', accent: 'sky', font: 'techy', radius: 'round', textSize: 'md' } },
	{ label: 'Storybook', emoji: '📖', patch: { theme: 'blossom', accent: 'rose', font: 'editorial', radius: 'soft', textSize: 'lg' } },
];

function isTextSize(value: unknown): value is TextSize {
	return value === 'sm' || value === 'md' || value === 'lg' || value === 'xl';
}

function isRadius(value: unknown): value is RadiusMode {
	return value === 'sharp' || value === 'soft' || value === 'round' || value === 'pill';
}

function isThemeId(value: unknown): value is string {
	return value === 'system' || (typeof value === 'string' && THEMES.some((theme) => theme.id === value));
}

function isAccentId(value: unknown): value is string {
	return value === 'theme' || value === 'custom' || (typeof value === 'string' && ACCENTS.some((accent) => accent.id === value));
}

/** Parse a stored appearance object. Returns null when the theme id is missing or unknown. */
export function parseStoredAppearance(raw: string): Appearance | null {
	let data: unknown;
	try {
		data = JSON.parse(raw);
	} catch {
		return null;
	}
	if (!data || typeof data !== 'object') return null;
	const record = data as Record<string, unknown>;
	if (!isThemeId(record.theme)) return null;
	const custom = typeof record.customAccent === 'string' && hexToHsl(record.customAccent)
		? record.customAccent
		: DEFAULT_APPEARANCE.customAccent;
	return {
		theme: record.theme,
		accent: isAccentId(record.accent) ? record.accent : DEFAULT_APPEARANCE.accent,
		customAccent: custom,
		font: typeof record.font === 'string' && FONTS.some((font) => font.id === record.font)
			? record.font
			: DEFAULT_APPEARANCE.font,
		textSize: isTextSize(record.textSize) ? record.textSize : DEFAULT_APPEARANCE.textSize,
		radius: isRadius(record.radius) ? record.radius : DEFAULT_APPEARANCE.radius,
		reduceMotion: record.reduceMotion === true,
	};
}

/** Map the old light/dark/system key onto Cream, Midnight, or system. */
export function appearanceFromLegacy(stored: string | null): Appearance {
	const theme = stored === 'light' ? 'cream' : stored === 'dark' ? 'midnight' : 'system';
	return { ...DEFAULT_APPEARANCE, theme };
}

export function readAppearanceFromStorage(): Appearance {
	if (typeof window === 'undefined') return DEFAULT_APPEARANCE;
	try {
		const stored = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
		if (stored) {
			const parsed = parseStoredAppearance(stored);
			if (parsed) return parsed;
		}
		return appearanceFromLegacy(window.localStorage.getItem(LEGACY_THEME_KEY));
	} catch {
		return DEFAULT_APPEARANCE;
	}
}

function ensureFont(fontId: string) {
	const href = FONT_STYLESHEETS[fontId];
	if (!href || typeof document === 'undefined') return;
	const elementId = `appearance-font-${fontId}`;
	if (document.getElementById(elementId)) return;
	const link = document.createElement('link');
	link.id = elementId;
	link.rel = 'stylesheet';
	link.href = href;
	document.head.appendChild(link);
}

export function applyAppearance(appearance: Appearance, prefersDark: boolean, systemReduceMotion = false) {
	const root = document.documentElement;
	const theme = resolveTheme(appearance, prefersDark);
	const set = (key: string, value: string) => root.style.setProperty(key, value);
	const { v } = theme;

	set('--background', v.bg);
	set('--foreground', v.fg);
	set('--card', v.card);
	set('--card-foreground', v.fg);
	set('--popover', v.card);
	set('--popover-foreground', v.fg);
	set('--secondary', v.secondary);
	set('--secondary-foreground', v.fg);
	set('--muted', v.muted);
	set('--muted-foreground', v.mutedFg);
	set('--border', v.border);
	set('--input', v.input);

	const { h, s, l } = accentCss(appearance, theme);
	const luminance = relativeLuminance(h, s, l);
	set('--primary', `${h} ${s}% ${l}%`);
	set('--ring', `${h} ${s}% ${l}%`);
	set('--primary-foreground', luminance > 0.42 ? '250 40% 8%' : '0 0% 100%');
	if (theme.dark) {
		set('--accent', `${h} ${Math.round(Math.min(s, 55) * 0.8)}% 20%`);
		set('--accent-foreground', `${h} ${Math.min(s, 90)}% 86%`);
	} else {
		set('--accent', `${h} ${Math.min(s, 90)}% 95%`);
		set('--accent-foreground', `${h} ${s}% ${Math.min(l, 38)}%`);
	}
	const brands = brandChannels(h, s, l);
	set('--brand-2', brands.brand2);
	set('--brand-3', brands.brand3);
	// Status inks follow the accent. The old fixed green, blue, and terra stay out.
	const toneForeground = relativeLuminance(h, Math.min(s, 72), theme.dark ? 64 : 36) > 0.42
		? '250 40% 8%'
		: '0 0% 100%';
	if (theme.dark) {
		set('--success', toneChannel(h, s, 62));
		set('--warning', toneChannel(h, s, 68));
		set('--info', toneChannel(h, s, 74));
	} else {
		set('--success', toneChannel(h, s, 32));
		set('--warning', toneChannel(h, s, 38));
		set('--info', toneChannel(h, s, 44));
	}
	set('--success-foreground', toneForeground);
	set('--warning-foreground', toneForeground);
	set('--info-foreground', toneForeground);

	const font = FONTS.find((item) => item.id === appearance.font) ?? FONTS[0];
	if (font) {
		ensureFont(font.id);
		set('--app-font-sans', font.sans);
		set('--app-font-display', font.display);
		set('--font-sans', font.sans);
		set('--font-display', font.display);
		set('--tracking', font.tracking);
	}

	const radius = RADII.find((item) => item.id === appearance.radius) ?? RADII[2];
	if (radius) {
		set('--radius', radius.radius);
		set('--btn-radius', radius.btn);
	}

	const size = TEXT_SIZES.find((item) => item.id === appearance.textSize) ?? TEXT_SIZES[1];
	if (size) root.style.fontSize = `${size.px}px`;
	root.classList.toggle('dark', theme.dark);
	root.classList.toggle('reduce-motion', appearance.reduceMotion || systemReduceMotion);
	root.dataset.theme = theme.id;
	root.style.colorScheme = theme.dark ? 'dark' : 'light';
}
